import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';

export type FiltroTemporal = '1d' | '2d' | '7d' | 'custom';

export interface RangoTemporal {
  desde: string; // ISO
  hasta: string; // ISO
}

export interface ICapaConfig {
  key: string;
  label: string;
  icon: string;
  countGetter?: () => number;
}

export interface IResumenPoligono {
  alertas: number;
  sirenas: number;
  vecinos?: number;
  controles?: number;
  truncados?: {
    alertas?: boolean;
    sirenas?: boolean;
    vecinos?: boolean;
  };
}

@Component({
  selector: 'app-flotante-alertas-seguridad',
  templateUrl: './flotante-alertas-seguridad.component.html',
  styleUrl: './flotante-alertas-seguridad.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlotanteAlertasSeguridadComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);

  @Input() tiposActivos: Set<string> = new Set();
  @Input() filtroTemporal: FiltroTemporal = '7d';
  @Input() rangoPersonalizado?: RangoTemporal;
  @Input() soloActivas = true;
  @Input() totalAlertas = 0;
  @Input() totalEventos = 0;
  @Input() totalSirenas = 0;
  @Input() dibujandoPoligono = false;
  @Input() buscandoPoligono = false;
  @Input() exportandoPoligono = false;
  @Input() resumenPoligono: IResumenPoligono | null = null;

  @Output() tiposActivosChange = new EventEmitter<Set<string>>();
  @Output() filtroTemporalChange = new EventEmitter<FiltroTemporal>();
  @Output() rangoPersonalizadoChange = new EventEmitter<RangoTemporal>();
  @Output() soloActivasChange = new EventEmitter<boolean>();
  @Output() iniciarPoligonoEvt = new EventEmitter<void>();
  @Output() cancelarPoligonoEvt = new EventEmitter<void>();
  @Output() limpiarPoligonoEvt = new EventEmitter<void>();
  @Output() exportarPoligonoEvt = new EventEmitter<void>();

  public expansionPanelExpanded = true;

  public readonly opcionesTemporal: { value: FiltroTemporal; label: string }[] =
    [
      { value: '1d', label: 'Día actual' },
      { value: '2d', label: 'Últimas 48 horas' },
      { value: '7d', label: 'Última semana' },
      { value: 'custom', label: 'Personalizado' },
    ];

  public capas: ICapaConfig[] = [
    {
      key: 'alertas',
      label: 'Alertas',
      icon: 'crisis_alert',
      countGetter: () => this.totalAlertas,
    },
    {
      key: 'sirenas',
      label: 'Sirenas',
      icon: 'campaign',
      countGetter: () => this.totalSirenas,
    },
    { key: 'zonas', label: 'Zonas', icon: 'crop_free' },
    { key: 'localidades', label: 'Localidades', icon: 'map' },
    { key: 'puntos', label: 'Puntos de interés', icon: 'place' },
    {
      key: 'eventos',
      label: 'Eventos',
      icon: 'report',
      countGetter: () => this.totalEventos,
    },
  ];

  public rangoLocal: { start: string; end: string } | null = null;
  public rangoError: string | null = null;

  public toggleTipo(key: string): void {
    const next = new Set(this.tiposActivos);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.tiposActivos = next;
    this.tiposActivosChange.emit(next);
    this.cdr.markForCheck();
  }

  public onFiltroChange(value: FiltroTemporal): void {
    this.filtroTemporal = value;
    this.filtroTemporalChange.emit(value);
    this.cdr.markForCheck();
  }

  public aplicarRangoPersonalizado(): void {
    this.rangoError = null;
    if (!this.rangoLocal?.start || !this.rangoLocal?.end) {
      this.rangoError = 'Seleccioná un rango';
      return;
    }
    const desde = new Date(this.rangoLocal.start);
    const hasta = new Date(this.rangoLocal.end);
    if (isNaN(desde.getTime()) || isNaN(hasta.getTime())) {
      this.rangoError = 'Fechas inválidas';
      return;
    }
    if (desde >= hasta) {
      this.rangoError = 'Inicio debe ser anterior a fin';
      return;
    }
    const diffDias = (hasta.getTime() - desde.getTime()) / 86_400_000;
    if (diffDias > 7) {
      this.rangoError = 'Rango máximo: 7 días';
      return;
    }
    const rango: RangoTemporal = {
      desde: desde.toISOString(),
      hasta: hasta.toISOString(),
    };
    this.rangoPersonalizado = rango;
    this.rangoPersonalizadoChange.emit(rango);
    this.cdr.markForCheck();
  }

  public formatCount(n: number): string {
    // Completo hasta 9.999; después "629 mil" y "1,1 M"
    if (n < 10_000) return n.toLocaleString('es-AR');
    if (n < 1_000_000) return Math.round(n / 1000) + ' mil';
    return (
      (n / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 }) +
      ' M'
    );
  }

  public iniciarPoligono(): void {
    this.iniciarPoligonoEvt.emit();
  }

  public cancelarPoligono(): void {
    this.cancelarPoligonoEvt.emit();
  }

  public limpiarPoligono(): void {
    this.limpiarPoligonoEvt.emit();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    const wasMobile = !this.expansionPanelExpanded;
    const isMobileNow = event.target.innerWidth <= 768;
    if (wasMobile !== isMobileNow) {
      this.expansionPanelExpanded = !isMobileNow;
    }
  }

  ngOnInit(): void {
    this.expansionPanelExpanded = window.innerWidth > 768;
  }
}
